import { BayLevelEnum, ForeAftEnum, } from "open-vessel-definition";
import { cloneObject, feetToMillimeters, pad2, pad3, roundDec, sortNumericAsc, } from "@tedivo/tedivo-pure-helpers";
import { sortRowsArray } from "../../sortRowsArray";
export class SmartCGs {
    constructor(bayLcgVcgTcgAndPairings) {
        this.bayLcgVcgTcgAndPairings = bayLcgVcgTcgAndPairings;
        this.smartBayBlocksByBay = {};
        this.offsetBottomBases = [];
        const { pairedBaysCalc: { pairedBays, unpairedBays }, } = bayLcgVcgTcgAndPairings;
        /** Create the smart bays */
        this.smartBayBlocks = [
            ...pairedBays.map((pb) => new SmartPairedBayBlock(pb.allBays, pb.level, bayLcgVcgTcgAndPairings)),
            ...unpairedBays.map((ub) => new SmartUnpairedBayBlock(ub.base, ub.level, bayLcgVcgTcgAndPairings)),
        ].sort((a, b) => a.tBaysStr.localeCompare(b.tBaysStr));
        /** Create the smart bays by bay (dictionary) */
        this.smartBayBlocks.forEach((smartBay) => {
            smartBay.getEnclosedBays().forEach((enclosedBay) => {
                if (!this.smartBayBlocksByBay[enclosedBay])
                    this.smartBayBlocksByBay[enclosedBay] = {};
                this.smartBayBlocksByBay[enclosedBay][smartBay.level] = smartBay;
            });
        });
    }
    setOffsetBottomBases(offsetBottomBases) {
        this.offsetBottomBases = offsetBottomBases;
        return this;
    }
    calculateCgsOfContainers(containers, options) {
        const verticalSeparation = (options === null || options === void 0 ? void 0 : options.verticalSeparation) || 0;
        const uniformHeight = options === null || options === void 0 ? void 0 : options.uniformHeight;
        const { minTierAbove } = this.bayLcgVcgTcgAndPairings;
        const errors = new Map();
        // Iterate over the containers and add them to the smart bays
        for (const container of containers) {
            const extendedContainer = extendContainer(container, minTierAbove);
            const { bay, level, pk } = extendedContainer;
            if (!this.smartBayBlocksByBay[bay]) {
                errors.set(pk, EInvalidContainerReasonEnum.INEXISTENT_BAY);
                continue;
            }
            const smartBayBlock = this.smartBayBlocksByBay[bay][level];
            if (!smartBayBlock) {
                errors.set(pk, EInvalidContainerReasonEnum.INEXISTENT_LEVEL);
                continue;
            }
            const error = smartBayBlock.addContainer(extendedContainer);
            if (error) {
                errors.set(pk, error);
                continue;
            }
        }
        const calculationsResult = this._doCalculations({ verticalSeparation, uniformHeight });
        // A container nothing could place keeps the others drawable: it is reported, not thrown
        calculationsResult.containersWithoutCgs.forEach((container) => {
            errors.set(container.pk, EInvalidContainerReasonEnum.MISSING_CGS);
        });
        return Object.assign({ errors }, calculationsResult);
    }
    _doCalculations(options) {
        const verticalSeparation = (options === null || options === void 0 ? void 0 : options.verticalSeparation) || 0;
        const uniformHeight = options === null || options === void 0 ? void 0 : options.uniformHeight;
        const cellsInfoCalculated = [];
        const bayLevelCgsInfo = {};
        const containerCgsByBayAndLevelAndRow = {};
        const containersWithoutCgs = [];
        let maxHeight = 0;
        let maxHeightWithSeparation = 0;
        let minBottomBase = Infinity;
        let bottomTier = Infinity;
        let portTcg = Infinity;
        let starboardTcg = -Infinity;
        let maxForeLcg = -Infinity;
        let minAftLcg = Infinity;
        const stackBottomsByBay = {};
        // Iterate over the smart bays
        this.smartBayBlocks
            .sort((a, b) => a.tBaysStr.localeCompare(b.tBaysStr))
            .forEach((smartBay) => {
            const stackResultsOfBay = smartBay.doCalculations({ verticalSeparation, uniformHeight });
            let maxHeightOfBay = 0;
            let maxHeightWithSeparationOfBay = 0;
            let minBottomBaseOfBay = Infinity;
            let bottomTierOfBay = Infinity;
            let portTcgOfBay = Infinity;
            let starboardTcgOfBay = -Infinity;
            let maxForeLcgOfBay = -Infinity;
            let minAftLcgOfBay = Infinity;
            let numCellsDefinedInBay = 0;
            // Iterate over the stacks ------------------------------------------------------------>
            const stackResultsKeysArray = Object.keys(stackResultsOfBay);
            stackResultsKeysArray.forEach((row) => {
                const { cellsInfo, containersWithoutCgs: withoutCgsInStack, tierAtTheBottom, stackBottomsByBayInStack, maxHeight, maxHeightWithSeparation, minBottomBase, } = stackResultsOfBay[row];
                containersWithoutCgs.push(...withoutCgsInStack);
                const keyPairedBayAndLevel = `${smartBay.tBaysStr}-${smartBay.level}-${row}`;
                if (cellsInfo.length === 0) {
                    return;
                }
                numCellsDefinedInBay += cellsInfo.length;
                // Iterate over the cells ---------------------------------------------------------------->
                cellsInfo.forEach((cellInfo) => {
                    const { size, cgs } = cellInfo;
                    const { tcg, lcg } = cgs !== null && cgs !== void 0 ? cgs : { tcg: undefined, lcg: undefined };
                    const sizeInMm = roundDec(feetToMillimeters(size / 2), 0);
                    const foreLcg = lcg !== undefined ? lcg + sizeInMm : undefined;
                    const aftLcg = lcg !== undefined ? lcg - sizeInMm : undefined;
                    if (tcg !== undefined && tcg < portTcgOfBay)
                        portTcgOfBay = tcg;
                    if (tcg !== undefined && tcg > starboardTcgOfBay)
                        starboardTcgOfBay = tcg;
                    if (foreLcg !== undefined && foreLcg > maxForeLcgOfBay)
                        maxForeLcgOfBay = foreLcg;
                    if (aftLcg !== undefined && aftLcg < minAftLcgOfBay)
                        minAftLcgOfBay = aftLcg;
                });
                // --end cells iteration ----------------------------------------------------------------->
                if (Number(tierAtTheBottom) < bottomTierOfBay)
                    bottomTierOfBay = Number(tierAtTheBottom);
                if (maxHeight !== Infinity && maxHeight > maxHeightOfBay)
                    maxHeightOfBay = maxHeight;
                if (maxHeightWithSeparation !== Infinity &&
                    maxHeightWithSeparation > maxHeightWithSeparationOfBay)
                    maxHeightWithSeparationOfBay = maxHeightWithSeparation;
                if (minBottomBase < minBottomBaseOfBay)
                    minBottomBaseOfBay = minBottomBase;
                if (stackBottomsByBayInStack) {
                    Object.keys(stackBottomsByBayInStack).forEach((key) => {
                        if (stackBottomsByBay[key]) {
                            stackBottomsByBay[key] = Math.min(stackBottomsByBay[key], stackBottomsByBayInStack[key]);
                        }
                        else {
                            stackBottomsByBay[key] = stackBottomsByBayInStack[key];
                        }
                    });
                }
                cellsInfoCalculated.push(...cellsInfo);
                containerCgsByBayAndLevelAndRow[keyPairedBayAndLevel] = cellsInfo;
            });
            // --end rows iteration ----------------------------------------------------------------->
            if (!bayLevelCgsInfo[smartBay.tBaysStr])
                bayLevelCgsInfo[smartBay.tBaysStr] = {};
            if (numCellsDefinedInBay > 0) {
                bayLevelCgsInfo[smartBay.tBaysStr][smartBay.level] = {
                    portTcg: portTcgOfBay,
                    starboardTcg: starboardTcgOfBay,
                    maxForeLcg: maxForeLcgOfBay,
                    minAftLcg: minAftLcgOfBay,
                    bottomBase: minBottomBaseOfBay,
                    bottomTier: pad2(bottomTierOfBay),
                    maxHeightAbsoluteOfBay: maxHeightOfBay,
                    maxHeightWithSeparationAbsoluteOfBay: maxHeightWithSeparationOfBay,
                };
                if (maxHeightOfBay > maxHeight)
                    maxHeight = maxHeightOfBay;
                if (maxHeightWithSeparationOfBay > maxHeightWithSeparation)
                    maxHeightWithSeparation = maxHeightWithSeparationOfBay;
                if (minBottomBaseOfBay < minBottomBase)
                    minBottomBase = minBottomBaseOfBay;
                if (bottomTierOfBay < bottomTier)
                    bottomTier = bottomTierOfBay;
                if (portTcgOfBay < portTcg)
                    portTcg = portTcgOfBay;
                if (starboardTcgOfBay > starboardTcg)
                    starboardTcg = starboardTcgOfBay;
                if (maxForeLcgOfBay > maxForeLcg)
                    maxForeLcg = maxForeLcgOfBay;
                if (minAftLcgOfBay < minAftLcg)
                    minAftLcg = minAftLcgOfBay;
            }
        });
        return {
            cellsInfoCalculated,
            containersWithoutCgs,
            maxHeight,
            maxHeightWithSeparation,
            minBottomBase,
            bottomTier,
            bayLevelCgsInfo,
            containerCgsByBayAndLevelAndRow,
            stackBottomsByBay,
        };
    }
}
// ------------------------------------------------------------------------------------------------
// Smart Bay Block
class SmartBayBlock {
    constructor(level, baySizesAndCgs, type) {
        this.fwdBay = undefined;
        this.boundaries = { minTier: Infinity, maxTier: -Infinity, maxRow: 0, centerLineRow: 0 };
        this.level = level;
        this.baySizesAndCgs = baySizesAndCgs;
        this.type = type;
        this.baysSet = new Set();
        const aBaysStr = [];
        this.baySizesAndCgs.forEach((bsc) => {
            aBaysStr.push(bsc.isoBay);
            if (bsc.minTier < this.boundaries.minTier)
                this.boundaries.minTier = bsc.minTier;
            if (bsc.maxTier > this.boundaries.maxTier)
                this.boundaries.maxTier = bsc.maxTier;
            if (bsc.maxRow > this.boundaries.maxRow)
                this.boundaries.maxRow = bsc.maxRow;
            if (bsc.centerLineRow)
                this.boundaries.centerLineRow = bsc.centerLineRow;
        });
        this.tBaysStr = aBaysStr.join("-");
    }
    getEnclosedBays() {
        return Array.from(this.baysSet).sort(sortNumericAsc);
    }
}
export class SmartUnpairedBayBlock extends SmartBayBlock {
    constructor(bay, level, bayLcgVcgTcgAndPairings) {
        super(level, [bayLcgVcgTcgAndPairings.bayLevelPositionsDict[`${bay}-${level}`]], "unpaired");
        this.bayLcgVcgTcgAndPairings = bayLcgVcgTcgAndPairings;
        this.smartBaysStacks = new Map();
        this.uniqueBlp = this.baySizesAndCgs[0];
        this.baysSet.add(bay);
        this.obtainCgsFromBSs = obtainCgsFromBSsGenerator([bay], level, bayLcgVcgTcgAndPairings);
    }
    addContainer(container) {
        var _a;
        const { row, tier } = container;
        const error = validateContainer(row, tier, this.boundaries);
        if (error)
            return error;
        if (!this.smartBaysStacks.has(row)) {
            this.smartBaysStacks.set(row, new SmartUnpairedBaysStack(row, this.uniqueBlp.isoBay, this.level, this.obtainCgsFromBSs));
        }
        return (_a = this.smartBaysStacks.get(row)) === null || _a === void 0 ? void 0 : _a.addContainer(container);
    }
    doCalculations({ verticalSeparation, uniformHeight, }) {
        const result = {};
        const blockBottom = getBlockBottom(Array.from(this.smartBaysStacks.values()));
        this.smartBaysStacks.forEach((stack, row) => {
            result[row] = stack.doCalculationsPerStack({
                verticalSeparation,
                blockBottom,
                uniformHeight,
            });
        });
        return result;
    }
    getSmartStacksFromPortToStarboard() {
        const stacks = Array.from(this.smartBaysStacks.values()).sort((a, b) => {
            return sortRowsArray(a.stack, b.stack);
        });
        return stacks;
    }
    getGridContent() {
        const gridContent = {};
        const allTiersKeys = [];
        const allStacksKeys = [];
        this.smartBaysStacks.forEach((stack, row) => {
            gridContent[row] = stack.getGridContent();
            allStacksKeys.push(row);
            allTiersKeys.push(...Object.keys(gridContent[row]));
        });
        return {
            gridContent,
            allStacksKeys: allStacksKeys.sort(sortRowsArray),
            allTiersKeys: Array.from(new Set(allTiersKeys)).sort(sortNumericAsc),
        };
    }
}
export class SmartPairedBayBlock extends SmartBayBlock {
    constructor(bays, level, bayLcgVcgTcgAndPairings) {
        super(level, bays.map((b) => bayLcgVcgTcgAndPairings.bayLevelPositionsDict[`${b}-${level}`]), "paired");
        this.smartBaysStacks = new Map();
        this.blpsByBay = {};
        this.blpOf40s = undefined;
        this.obtainCgsFromBSs = obtainCgsFromBSsGenerator(bays, level, bayLcgVcgTcgAndPairings);
        // Exit 0. If there are no bays, return
        if (bays.length === 0) {
            return;
        }
        const iBays = bays.map((b) => Number(b));
        const minBay = Math.min(...iBays);
        const maxBay = Math.max(...iBays);
        for (let i = minBay; i <= maxBay; i++) {
            this.baysSet.add(pad3(i));
        }
        if (this.baysSet.size > 3) {
            throw new Error("More than 3 bays are not supported");
        }
        this.fwdBay = minBay;
        this.blpOf40s = this.baySizesAndCgs.find((bsc) => bsc.maxSize >= 40);
        this.baySizesAndCgs.forEach((bsc) => {
            this.blpsByBay[bsc.isoBay] = bsc;
        });
    }
    addContainer(container) {
        var _a, _b;
        const { row, tier } = container;
        const error = validateContainer(row, tier, this.boundaries);
        if (error)
            return error;
        if (!this.smartBaysStacks.has(row)) {
            this.smartBaysStacks.set(row, new SmartPairedBaysStack(row, this.level, this.fwdBay, (_a = this.blpOf40s) === null || _a === void 0 ? void 0 : _a.isoBay, Array.from(this.baysSet).filter((b) => Number(b) % 2 === 1), this.obtainCgsFromBSs));
        }
        return (_b = this.smartBaysStacks.get(row)) === null || _b === void 0 ? void 0 : _b.addContainer(container);
    }
    doCalculations({ verticalSeparation, uniformHeight, }) {
        const result = {};
        const stackKeys = Array.from(this.smartBaysStacks.keys()).sort(sortRowsArray);
        const blockBottom = getBlockBottom(Array.from(this.smartBaysStacks.values()));
        stackKeys.forEach((row) => {
            const stack = this.smartBaysStacks.get(row);
            if (stack) {
                result[row] = stack.doCalculationsPerStack({
                    verticalSeparation,
                    blockBottom,
                    uniformHeight,
                });
            }
        });
        return result;
    }
    getSmartStacksFromPortToStarboard() {
        const stacks = Array.from(this.smartBaysStacks.values()).sort((a, b) => {
            return sortRowsArray(a.stack, b.stack);
        });
        return stacks;
    }
    getGridContent() {
        const gridContent = {};
        const allTiersKeys = [];
        const allStacksKeys = [];
        this.smartBaysStacks.forEach((stack, row) => {
            gridContent[row] = stack.getGridContent();
            allStacksKeys.push(row);
            allTiersKeys.push(...Object.keys(gridContent[row]));
        });
        return {
            gridContent,
            allStacksKeys: allStacksKeys.sort(sortRowsArray),
            allTiersKeys: Array.from(new Set(allTiersKeys)).sort(sortNumericAsc),
        };
    }
}
// ------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
// Smart Bays Stack
class SmartUnpairedBaysStack {
    constructor(stack, bay, level, obtainCgsFromBSs) {
        this.stack = stack;
        this.bay = bay;
        this.level = level;
        this.obtainCgsFromBSs = obtainCgsFromBSs;
        this.tiers = new Map();
        this.tierAtTheBottom = undefined;
    }
    clearContainers() {
        this.tiers.clear();
        this.tierAtTheBottom = undefined;
    }
    addContainer(container) {
        const { tier } = container;
        const iTier = Number(tier);
        if (this.tiers.has(iTier)) {
            return EInvalidContainerReasonEnum.REPEATED_CELL;
        }
        if (this.tierAtTheBottom === undefined || iTier < this.tierAtTheBottom) {
            this.tierAtTheBottom = iTier;
        }
        this.tiers.set(iTier, new UnpairedStackContainer(tier, container));
        return undefined;
    }
    /** Bottom base and lowest tier of this stack, as read from the OVD (or estimated) */
    getStackBottom() {
        var _a, _b;
        if (this.tierAtTheBottom === undefined)
            return undefined;
        const tierAtTheBottom = pad2(this.tierAtTheBottom);
        const bottomBase = (_b = (_a = this.obtainCgsFromBSs(this.stack, this.bay, 20, tierAtTheBottom, tierAtTheBottom)) === null || _a === void 0 ? void 0 : _a.stackBottomBase) !== null && _b !== void 0 ? _b : 0;
        return { bottomBase, tierAtTheBottom: this.tierAtTheBottom };
    }
    doCalculationsPerStack({ verticalSeparation, blockBottom, uniformHeight, }) {
        var _a;
        const stackBottom = this.getStackBottom();
        if (!stackBottom) {
            throw new Error("tierAtTheBottom is undefined");
        }
        const tierAtTheBottom = pad2(stackBottom.tierAtTheBottom);
        const stackBottomBase = (_a = stackBottom.bottomBase) !== null && _a !== void 0 ? _a : 0;
        let maxHeight = stackBottomBase;
        let maxHeightWithSeparation = stackBottomBase;
        let placedCount = 0;
        const tiers = Array.from(this.tiers.keys()).sort(sortNumericAsc);
        for (const tier of tiers) {
            const cst = this.tiers.get(tier);
            if (!cst)
                continue;
            const container = cst.container;
            const calcCgs = this.obtainCgsFromBSs(this.stack, this.bay, container.size, container.tier, tierAtTheBottom);
            if (!calcCgs)
                continue; // ---> Omit tier and continue to the next tier
            const { tcg, lcg } = calcCgs;
            const seat = seatContainer({
                tier: container.tier,
                accumulated: { bottomBase: maxHeight, bottomBaseWithSeparation: maxHeightWithSeparation },
                blockBottom,
                uniformHeight,
                verticalSeparation,
            });
            container.cgs = {
                size: container.size,
                lcg,
                tcg,
                bottomBase: seat.bottomBase,
                bottomBaseWithSeparation: seat.bottomBaseWithSeparation,
                bottomTier: tierAtTheBottom,
                tierBottomBase: stackBottomBase,
                stackBottomBase: stackBottomBase,
                containersBelowCount: placedCount,
                tierIndex: seat.tierIndex,
                isoOddBay: this.bay,
                bayLevel: container.isAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW,
            };
            const height = uniformHeight !== null && uniformHeight !== void 0 ? uniformHeight : container.height;
            maxHeight = seat.bottomBase + height;
            maxHeightWithSeparation = seat.bottomBaseWithSeparation + height + verticalSeparation;
            placedCount += 1;
        }
        const containersWithoutCgs = this.evictContainersWithoutCgs();
        return {
            cellsInfo: Array.from(this.tiers.values()).map((cst) => cst.container),
            containersWithoutCgs,
            maxHeight,
            maxHeightWithSeparation: maxHeightWithSeparation - verticalSeparation,
            minBottomBase: stackBottomBase,
            tierAtTheBottom,
            stackBottomsByBayInStack: { [`${this.bay}-${this.level}`]: stackBottomBase },
        };
    }
    /**
     * A container the calculation skipped (no CGs could be derived, typically a row the
     * Bay does not have) is dropped from the stack and reported, so the views never meet
     * a container without CGs.
     */
    evictContainersWithoutCgs() {
        const evicted = [];
        this.tiers.forEach((cst, tier) => {
            if (cst.container.cgs === undefined) {
                evicted.push(cst.container);
                this.tiers.delete(tier);
            }
        });
        return evicted;
    }
    getTiersFromBottomTierToTop() {
        return Array.from(this.tiers.values()).sort((a, b) => {
            return sortNumericAsc(a.tier, b.tier);
        });
    }
    getGridContent() {
        const gridContent = {};
        this.tiers.forEach((content, tier) => {
            gridContent[pad2(tier)] = content;
        });
        return gridContent;
    }
}
class SmartPairedBaysStack {
    constructor(stack, level, fwdBay = undefined, bayOf40 = undefined, oddBays = [], obtainCgsFromBSs) {
        this.stack = stack;
        this.level = level;
        this.fwdBay = fwdBay;
        this.bayOf40 = bayOf40;
        this.oddBays = oddBays;
        this.obtainCgsFromBSs = obtainCgsFromBSs;
        this.tiers = new Map();
        this.bbsAtTheBottomByBay = {};
        this.tiersAtTheBottomByBay = {};
    }
    clearContainers() {
        this.tiers.clear();
        this.tierAtTheBottom = undefined;
    }
    addContainer(container) {
        const { tier } = container;
        const iTier = Number(tier);
        let tierMapOfBays = this.tiers.get(iTier);
        if (!tierMapOfBays) {
            tierMapOfBays = new PairedStackContainer(tier, this.fwdBay);
            this.tiers.set(iTier, tierMapOfBays);
        }
        const error = tierMapOfBays.addContainer(container);
        if (error)
            return error;
        if (this.tierAtTheBottom === undefined || iTier < this.tierAtTheBottom) {
            this.tierAtTheBottom = iTier;
        }
        const bayRef = container.size >= 40 ? this.bayOf40 : container.bay;
        if (bayRef !== undefined &&
            (this.tiersAtTheBottomByBay[bayRef] === undefined ||
                tier < this.tiersAtTheBottomByBay[bayRef])) {
            this.tiersAtTheBottomByBay[bayRef] = tier;
        }
        return undefined;
    }
    /** Bottom bases of the stack, per bay, as read from the OVD (or estimated) */
    computeStackBottomsByBay() {
        if (this.tierAtTheBottom === undefined)
            return {};
        const tierAtTheBottom = pad2(this.tierAtTheBottom);
        // Get the tiers, from the bottom to the top
        const iAllTiers = Array.from(this.tiers.keys()).sort(sortNumericAsc);
        const firstCst = this.tiers.get(iAllTiers[0]);
        return this.oddBays.reduce((acc, bay) => {
            var _a, _b;
            if (!firstCst)
                return acc;
            const tb = firstCst.content === EContentOfPairedStackContainer.ONE40
                ? tierAtTheBottom
                : this.tiersAtTheBottomByBay[bay];
            if (tb === undefined)
                return acc;
            const sbb = (_b = ((_a = this.obtainCgsFromBSs(this.stack, bay, 20, tb, tb)) !== null && _a !== void 0 ? _a : this.obtainCgsFromBSs(this.stack, bay, 40, tb, tb))) === null || _b === void 0 ? void 0 : _b.stackBottomBase;
            if (sbb !== undefined && sbb !== Infinity) {
                acc[bay] = sbb;
            }
            return acc;
        }, {});
    }
    /** Lowest bottom base (of both bays) and lowest tier of this stack */
    getStackBottom() {
        if (this.tierAtTheBottom === undefined)
            return undefined;
        const bottoms = Object.values(this.computeStackBottomsByBay());
        return {
            bottomBase: bottoms.length ? Math.min(...bottoms) : undefined,
            tierAtTheBottom: this.tierAtTheBottom,
        };
    }
    doCalculationsPerStack({ verticalSeparation, blockBottom, uniformHeight, }) {
        var _a, _b, _c, _d;
        if (this.tierAtTheBottom === undefined) {
            throw new Error("tierAtTheBottom is undefined");
        }
        const tierAtTheBottom = pad2(this.tierAtTheBottom);
        // Get the tiers, from the bottom to the top
        const iAllTiers = Array.from(this.tiers.keys()).sort(sortNumericAsc);
        /** Heights by bay. They start with the bottom bases of the tiers */
        const heightsByBay = this.computeStackBottomsByBay();
        /** Heights with separation by bay */
        const heightsWithSeparationByBay = cloneObject(heightsByBay);
        /** Copy with the bottom bases of the tiers by bay */
        const stackBottomsByBay = cloneObject(heightsByBay);
        Object.freeze(stackBottomsByBay);
        /** Number of containers already placed in the stack, per bay */
        const placedCountByBay = {};
        const [fwdBay, aftBay] = this.oddBays;
        const bayOf40 = this.bayOf40;
        for (const iTier of iAllTiers) {
            const cst = this.tiers.get(iTier);
            if (!cst || cst.content === EContentOfPairedStackContainer.NONE)
                continue; // ---> Omit tier and continue to the next tier
            if (cst.content === EContentOfPairedStackContainer.ONE20_FWD ||
                cst.content === EContentOfPairedStackContainer.ONE20_AFT ||
                cst.content === EContentOfPairedStackContainer.TWO20) {
                for (const bay of Object.keys(cst.contsByBay)) {
                    const container = cst.contsByBay[bay];
                    const calcCgs = this.obtainCgsFromBSs(this.stack, bay, container.size, container.tier, tierAtTheBottom);
                    if (!calcCgs)
                        continue; // ---> Omit tier and continue to the next tier
                    const { tcg, lcg } = calcCgs;
                    const seat = seatContainer({
                        tier: container.tier,
                        accumulated: {
                            bottomBase: heightsByBay[bay],
                            bottomBaseWithSeparation: heightsWithSeparationByBay[bay],
                        },
                        blockBottom,
                        uniformHeight,
                        verticalSeparation,
                    });
                    container.cgs = {
                        size: container.size,
                        lcg,
                        tcg,
                        bottomBase: seat.bottomBase,
                        bottomBaseWithSeparation: seat.bottomBaseWithSeparation,
                        bottomTier: tierAtTheBottom,
                        tierBottomBase: stackBottomsByBay[bay],
                        stackBottomBase: stackBottomsByBay[bay],
                        containersBelowCount: (_a = placedCountByBay[bay]) !== null && _a !== void 0 ? _a : 0,
                        tierIndex: seat.tierIndex,
                        bayLevel: container.isAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW,
                        isoOddBay: bay,
                        extending: bay === fwdBay ? ForeAftEnum.AFT : ForeAftEnum.FWD,
                    };
                    if (this.bbsAtTheBottomByBay[bay] === undefined ||
                        seat.bottomBase < this.bbsAtTheBottomByBay[bay]) {
                        this.bbsAtTheBottomByBay[bay] = seat.bottomBase;
                    }
                    const height = uniformHeight !== null && uniformHeight !== void 0 ? uniformHeight : container.height;
                    heightsByBay[bay] = seat.bottomBase + height;
                    heightsWithSeparationByBay[bay] =
                        seat.bottomBaseWithSeparation + height + verticalSeparation;
                    placedCountByBay[bay] = ((_b = placedCountByBay[bay]) !== null && _b !== void 0 ? _b : 0) + 1;
                }
            }
            else if (cst.content === EContentOfPairedStackContainer.ONE40) {
                if (!bayOf40)
                    continue; // ---> Omit tier and continue to the next tier
                const container = cst.containers[0];
                const calcCgs = this.obtainCgsFromBSs(this.stack, bayOf40, container.size, container.tier, tierAtTheBottom);
                if (!calcCgs)
                    continue; // ---> Omit tier and continue to the next tier
                const { tcg, lcg } = calcCgs;
                // For this container, we need to use the max bottom base of the previous fwd and aft bays
                const seat = seatContainer({
                    tier: container.tier,
                    accumulated: {
                        bottomBase: Math.max(...Object.values(heightsByBay)),
                        bottomBaseWithSeparation: Math.max(...Object.values(heightsWithSeparationByBay)),
                    },
                    blockBottom,
                    uniformHeight,
                    verticalSeparation,
                });
                const maxBB = seat.bottomBase;
                const maxBBWithSeparation = seat.bottomBaseWithSeparation;
                const containersBelow40 = Math.max((_c = placedCountByBay[fwdBay]) !== null && _c !== void 0 ? _c : 0, (_d = placedCountByBay[aftBay]) !== null && _d !== void 0 ? _d : 0);
                container.cgs = {
                    size: container.size,
                    lcg,
                    tcg,
                    bottomTier: tierAtTheBottom,
                    bottomBase: maxBB,
                    bottomBaseWithSeparation: maxBBWithSeparation,
                    tierBottomBase: stackBottomsByBay[bayOf40],
                    stackBottomBase: Math.min(...Object.values(stackBottomsByBay)),
                    containersBelowCount: containersBelow40,
                    tierIndex: seat.tierIndex,
                    isoOddBay: bayOf40,
                    extending: bayOf40 === fwdBay ? ForeAftEnum.AFT : ForeAftEnum.FWD,
                    bayLevel: container.isAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW,
                };
                if (this.bbsAtTheBottomByBay[fwdBay] === undefined ||
                    maxBB < this.bbsAtTheBottomByBay[fwdBay]) {
                    this.bbsAtTheBottomByBay[fwdBay] = maxBB;
                }
                if (this.bbsAtTheBottomByBay[aftBay] === undefined ||
                    maxBB < this.bbsAtTheBottomByBay[aftBay]) {
                    this.bbsAtTheBottomByBay[aftBay] = maxBB;
                }
                const height = uniformHeight !== null && uniformHeight !== void 0 ? uniformHeight : container.height;
                heightsByBay[fwdBay] = maxBB + height;
                heightsByBay[aftBay] = maxBB + height;
                heightsWithSeparationByBay[fwdBay] = maxBBWithSeparation + height + verticalSeparation;
                heightsWithSeparationByBay[aftBay] = maxBBWithSeparation + height + verticalSeparation;
                placedCountByBay[fwdBay] = containersBelow40 + 1;
                placedCountByBay[aftBay] = containersBelow40 + 1;
            }
        }
        const containersWithoutCgs = this.evictContainersWithoutCgs();
        return {
            cellsInfo: Array.from(this.tiers.values()).flatMap((cst) => Object.values(cst.contsByBay)),
            containersWithoutCgs,
            maxHeight: Math.max(...Object.values(heightsByBay)),
            maxHeightWithSeparation: Math.max(...Object.values(heightsWithSeparationByBay)) - verticalSeparation,
            minBottomBase: Math.min(...Object.values(stackBottomsByBay)),
            tierAtTheBottom,
            stackBottomsByBayInStack: Object.keys(this.bbsAtTheBottomByBay).reduce((acc, key) => {
                acc[`${key}-${this.level}`] = this.bbsAtTheBottomByBay[key];
                return acc;
            }, {}),
        };
    }
    /** See `SmartUnpairedBaysStack.evictContainersWithoutCgs` */
    evictContainersWithoutCgs() {
        const evicted = [];
        this.tiers.forEach((cst, tier) => {
            Object.keys(cst.contsByBay).forEach((bay) => {
                const container = cst.contsByBay[bay];
                if (container.cgs === undefined) {
                    evicted.push(container);
                    delete cst.contsByBay[bay];
                }
            });
            if (Object.keys(cst.contsByBay).length === 0)
                this.tiers.delete(tier);
        });
        return evicted;
    }
    getTiersFromBottomTierToTop() {
        return Array.from(this.tiers.values()).sort((a, b) => {
            return sortNumericAsc(a.tier, b.tier);
        });
    }
    getGridContent() {
        const gridContent = {};
        this.tiers.forEach((content, tier) => {
            gridContent[pad2(tier)] = content;
        });
        return gridContent;
    }
}
class StackContainer {
    constructor(tier) {
        this.tier = tier;
        this.content = EContentOfPairedStackContainer.NONE;
        this.maxSize = 0;
    }
}
export class UnpairedStackContainer extends StackContainer {
    constructor(tier, container) {
        super(tier);
        this.container = container;
        this.contsByBay = {};
        if (container.size < 40) {
            this.content = EContentOfPairedStackContainer.ONE20_FWD;
        }
        this.contsByBay[container.bay] = container;
        this.maxSize = container.size;
    }
    get containers() {
        return [this.container];
    }
}
export class PairedStackContainer extends StackContainer {
    constructor(tier, fwdBay = undefined) {
        super(tier);
        this.fwdBay = fwdBay;
        this.contsByBay = {};
    }
    addContainer(containerToAdd) {
        const { bay } = containerToAdd;
        const contsByBay = this.contsByBay;
        if (this.maxSize === undefined || containerToAdd.size > this.maxSize) {
            this.maxSize = containerToAdd.size;
        }
        if (
        /** 2.1 If the tier is empty, add the container to the tier */
        this.content === EContentOfPairedStackContainer.NONE) {
            contsByBay[bay] = containerToAdd;
            this.content =
                containerToAdd.size < 40
                    ? containerToAdd.iBay === this.fwdBay
                        ? EContentOfPairedStackContainer.ONE20_FWD
                        : EContentOfPairedStackContainer.ONE20_AFT
                    : EContentOfPairedStackContainer.ONE40;
            return undefined;
        }
        else if (
        // 2.2 If the tier has only one container (ONE20)
        this.content === EContentOfPairedStackContainer.ONE20_FWD ||
            this.content === EContentOfPairedStackContainer.ONE20_AFT) {
            if (containerToAdd.size < 40) {
                // The container to add is a 20ft
                // Check that there isn't a 20ft container in the same bay
                if (contsByBay[bay]) {
                    return EInvalidContainerReasonEnum.REPEATED_CELL;
                }
                this.content = EContentOfPairedStackContainer.TWO20;
                contsByBay[bay] = containerToAdd;
                return undefined;
            }
            else {
                // The container to add is a 40ft. But the tier already has a 20ft
                return EInvalidContainerReasonEnum.OCCUPIED_SPACE;
            }
        }
        else {
            // this.content is ONE40 or TWO20, so the tier is already full
            return EInvalidContainerReasonEnum.OCCUPIED_SPACE;
        }
    }
    get containers() {
        return Object.values(this.contsByBay);
    }
}
function validateContainer(row, tier, boundaries) {
    const iTier = Number(tier);
    const iRow = Number(row);
    const isValidTier = iTier >= boundaries.minTier && iTier <= boundaries.maxTier;
    if (!isValidTier || iTier % 2 === 1) {
        return EInvalidContainerReasonEnum.INEXISTENT_TIER;
    }
    if (iRow < 0 || iRow > boundaries.maxRow || (!boundaries.centerLineRow && iRow === 0)) {
        return EInvalidContainerReasonEnum.INEXISTENT_ROW;
    }
    return undefined;
}
function extendContainer(container, minTierAbove) {
    const bay = container.position.substring(0, 3);
    const row = container.position.substring(3, 5);
    const tier = container.position.substring(5);
    const level = Number(tier) >= minTierAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW;
    const iBay = Number(bay);
    return Object.assign(Object.assign({}, container), { bay,
        row,
        tier,
        level,
        iBay });
}
function obtainCgsFromBSsGenerator(allBays, level, bayLcgVcgTcgAndPairings) {
    var _a;
    const blps = (_a = Array.from(allBays).map((pb) => bayLcgVcgTcgAndPairings.bayLevelPositionsDict[`${pb}-${level}`])) !== null && _a !== void 0 ? _a : [];
    return function obtainCgsFromBSs(row, bay, size, tier, lowestTier) {
        var _a, _b, _c;
        const blpToUse = blps.find((blp) => (size < 40 ? blp.isoBay === bay : blp.maxSize >= 40));
        if (!blpToUse) {
            console.log("blpToUse is undefined", bay, row, tier, blps);
            return undefined;
        }
        // Get the average CGs of the row, so we can interpolate the some values if missing
        const avgCGsOfRow = blpToUse.getAvgCGs(row);
        // Find a valid TCG for the row. It should only be one.
        const tcg = bayLcgVcgTcgAndPairings.missingImportantTcgs
            ? bayLcgVcgTcgAndPairings.getMissingTcg(row, blpToUse.isoBay, blpToUse.level)
            : avgCGsOfRow === null || avgCGsOfRow === void 0 ? void 0 : avgCGsOfRow.avgTcg;
        if (tcg === undefined)
            return undefined;
        // Find a valid bottom base for the row
        const containerBottomBase = bayLcgVcgTcgAndPairings.missingImportantVcgs
            ? bayLcgVcgTcgAndPairings.getMissingBottomBase(tier)
            : avgCGsOfRow === null || avgCGsOfRow === void 0 ? void 0 : avgCGsOfRow.lowestBottomBase;
        if (containerBottomBase === undefined)
            return undefined;
        // Find a valid LCG for the container
        const lcg = bayLcgVcgTcgAndPairings.missingImportantLcgs
            ? getLcgFromMissing(blpToUse, size, blpToUse.pairedBay)
            : (_c = (_b = (_a = blpToUse.getCellInfo(`${row}${tier}`)) === null || _a === void 0 ? void 0 : _a.sizes) === null || _b === void 0 ? void 0 : _b.get(size)) === null || _c === void 0 ? void 0 : _c.lcg;
        const stackBottomBase = bayLcgVcgTcgAndPairings.missingImportantVcgs
            ? bayLcgVcgTcgAndPairings.getMissingBottomBase(lowestTier)
            : avgCGsOfRow === null || avgCGsOfRow === void 0 ? void 0 : avgCGsOfRow.lowestBottomBase;
        return {
            lcg,
            tcg,
            containerBottomBase,
            stackBottomBase,
        };
        function getLcgFromMissing(blp, size, extending) {
            let lcg = bayLcgVcgTcgAndPairings.getMissing20Lcg(blp.isoBay, size);
            if (lcg === undefined) {
                const lcgT = bayLcgVcgTcgAndPairings.getMissing20Lcg(blp.isoBay, 20);
                if (lcgT !== undefined) {
                    if (extending) {
                        const difSizeMiddle = feetToMillimeters((size - 20) * 0.5);
                        lcg = extending === ForeAftEnum.FWD ? lcgT + difSizeMiddle : lcgT - difSizeMiddle;
                    }
                    else {
                        lcg = lcgT;
                    }
                }
            }
            return lcg;
        }
    };
}
function getBlockBottom(stacks) {
    let floor = Infinity;
    let bottomTier = Infinity;
    stacks.forEach((stack) => {
        const bottom = stack.getStackBottom();
        if (!bottom)
            return;
        if (bottom.bottomBase !== undefined && bottom.bottomBase < floor)
            floor = bottom.bottomBase;
        if (bottom.tierAtTheBottom < bottomTier)
            bottomTier = bottom.tierAtTheBottom;
    });
    return {
        floor: floor === Infinity ? 0 : floor,
        bottomTier: bottomTier === Infinity ? 0 : bottomTier,
    };
}
/**
 * Where a container sits. With `uniformHeight` the seat is derived from the tier (see
 * `TSmartCgsCalcOptions`); otherwise it is the accumulated height of the stack below it.
 */
function seatContainer({ tier, accumulated, blockBottom, uniformHeight, verticalSeparation, }) {
    // Tiers step by 2 (odd numbers are reserved for half-heights)
    const tierIndex = Math.max(0, (Number(tier) - blockBottom.bottomTier) / 2);
    if (uniformHeight === undefined) {
        return Object.assign(Object.assign({}, accumulated), { tierIndex });
    }
    return {
        bottomBase: blockBottom.floor + tierIndex * uniformHeight,
        bottomBaseWithSeparation: blockBottom.floor + tierIndex * (uniformHeight + verticalSeparation),
        tierIndex,
    };
}
export var EInvalidContainerReasonEnum;
(function (EInvalidContainerReasonEnum) {
    EInvalidContainerReasonEnum["REPEATED_CELL"] = "REPEATED_CELL";
    EInvalidContainerReasonEnum["INEXISTENT_BAY"] = "INEXISTENT_BAY";
    EInvalidContainerReasonEnum["INEXISTENT_LEVEL"] = "INEXISTENT_LEVEL";
    EInvalidContainerReasonEnum["INEXISTENT_ROW"] = "INEXISTENT_ROW";
    EInvalidContainerReasonEnum["INEXISTENT_TIER"] = "INEXISTENT_TIER";
    EInvalidContainerReasonEnum["INVALID_POSITION"] = "INVALID_POSITION";
    EInvalidContainerReasonEnum["OCCUPIED_SPACE"] = "OCCUPIED_SPACE";
    /** No CGs could be derived for the container, so it was left out of the calculation */
    EInvalidContainerReasonEnum["MISSING_CGS"] = "MISSING_CGS";
    EInvalidContainerReasonEnum["UNKNOWN"] = "UNKNOWN";
})(EInvalidContainerReasonEnum || (EInvalidContainerReasonEnum = {}));
export var EContentOfPairedStackContainer;
(function (EContentOfPairedStackContainer) {
    EContentOfPairedStackContainer["NONE"] = "NONE";
    EContentOfPairedStackContainer["ONE20_FWD"] = "ONE20_FWD";
    EContentOfPairedStackContainer["ONE20_AFT"] = "ONE20_AFT";
    EContentOfPairedStackContainer["ONE40"] = "ONE40";
    EContentOfPairedStackContainer["TWO20"] = "TWO20";
})(EContentOfPairedStackContainer || (EContentOfPairedStackContainer = {}));
